import { Alert, Alerts as AlertsApi, AlertSeverity, AlertStatus, AlertType, enumKeys, getAlertTypeDisplayName, ResolveBody } from "@app/shared";
import { FC, useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { Pages } from ".";
import { toastError, toastSuccess } from "../../helpers";
import { useAlertOrganizationScope, useConfirmDialog, useEditModelDialog, useFetch } from "../../hooks";
import { useAlertsUiVisible } from "../../hooks/useAlertsUiVisible";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { IconLink, Icons, PageContainer, SectionIntro, SortableTable, StatusDot } from "../shared";
import { DropDownMenu } from "../shared/DropDownMenu";
import { Select, TextArea } from "../shared/inputs";
import { alertTypeLabel, enumLabel, severityDotStatus, severityLabel, zonedTime } from "./notifications/alertDisplay";
import { useAlertScopeOptions } from "./notifications/AlertScope";

const alertTypeFilterOptions = enumKeys(AlertType).map((key) => ({ value: (AlertType[key] as number).toString(), label: getAlertTypeDisplayName(key) ?? key }));

// Device-health alerts for the current organization. Status and severity stay server-side
// filters rather than table filters: the API only returns non-resolved alerts unless a
// status is named, so resolved ones can't be reached by filtering what's already loaded.
export const Alerts: FC = () => {
    // One switch, shared with the top-nav bell, so the badge above the table always counts these rows.
    // A superadmin's read scope is the whole fleet, so "all organizations" is simply not narrowing the
    // query; everyone else has no switch to flip and stays scoped to the org they're viewing.
    const { allOrganizations, setAllOrganizations, canSeeAllOrganizations, organizationId: scopedOrganizationId, ready: scopeReady } = useAlertOrganizationScope();
    const visible = useAlertsUiVisible();
    const [status, setStatus] = useState<string>("");
    const [minSeverity, setMinSeverity] = useState<string>("");
    // Location and device narrowing are server-side filters the API already supports; scoping the
    // query beats client-side filtering because the response is capped (take=200 newest), so a busy
    // org's older alerts for one device would otherwise never be in the page to filter.
    const [facilityId, setFacilityId] = useState<string>("");
    const [deviceId, setDeviceId] = useState<string>("");

    // Notification emails and webhooks link here as /alerts?alertId=..., so arriving with that
    // parameter means "show me this one". It's fetched by id rather than filtered out of the list:
    // the list returns only non-resolved alerts by default and is capped at the 200 newest, and the
    // alert someone is clicking through to is very often already resolved — that resolution is what
    // sent them the email.
    const [searchParams, setSearchParams] = useSearchParams();
    const focusedAlertId = searchParams.get("alertId") ?? "";

    const [alerts, , alertHelpers] = useFetch(
        () => {
            if (!visible || !scopeReady) return Promise.resolve([]);
            if (focusedAlertId) return AlertsApi.getAlert(focusedAlertId).then((a) => (a ? [a] : []));
            return AlertsApi.list(
                scopedOrganizationId,
                facilityId === "" ? undefined : facilityId,
                deviceId === "" ? undefined : deviceId,
                status === "" ? undefined : (Number(status) as AlertStatus),
                minSeverity === "" ? undefined : (Number(minSeverity) as AlertSeverity)
            );
        },
        [visible, scopeReady, focusedAlertId, scopedOrganizationId, facilityId, deviceId, status, minSeverity]
    );

    const showAllAlerts = useCallback(() => {
        // Drops only this parameter, so any unrelated query state on the URL survives.
        searchParams.delete("alertId");
        setSearchParams(searchParams, { replace: true });
    }, [searchParams, setSearchParams]);
    // Only for the Location and Device dropdowns, which the fleet-wide view doesn't offer — see the
    // `enabled` argument's note in useAlertScopeOptions for why it isn't simply fetched unscoped. The
    // table's own Location and Device cells read the names off the alert row, so they are correct in
    // both views without any of this.
    const { facilityOptions, deviceOptions } = useAlertScopeOptions(scopedOrganizationId, true, !allOrganizations);

    // Distinct organizations actually present in the loaded page. Built from the rows rather than from
    // the full organization list because this is a client-side filter over what's on screen: offering
    // organizations with no alerts in the page would only ever filter the table to nothing.
    const organizationFilterOptions = Array.from(new Set((alerts ?? []).map((a) => a.organizationName).filter((n): n is string => !!n)))
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({ value: name, label: name }));

    // A location or device chosen under one scope filters to nothing under the other, and a filter
    // that silently matches no rows reads as "there are no alerts".
    useEffect(() => {
        setFacilityId("");
        setDeviceId("");
    }, [allOrganizations]);
    const resolveDialog = useEditModelDialog(ResolveBody);
    const confirmDialog = useConfirmDialog();

    // Only the table is refreshed here. The top-nav bell can't be refreshed from this component —
    // AlertsProvider wraps the inside of DashboardLayout, so a page sits above it and useAlerts()
    // would hand back the inert default context. The bell instead follows the server's org-wide
    // AlertsChanged signal, which fires for this acknowledge/resolve as well as for changes made by
    // another admin or by the auto-resolve job.
    const reloadAlerts = useCallback(async () => {
        alertHelpers.refreshData();
    }, [alertHelpers]);

    const handleAcknowledgeOnClick = (alert: Alert | undefined) => {
        if (!alert) return;
        confirmDialog.show("Acknowledge Alert?", `${alert.message ?? alertTypeLabel(alert.alertType)} will be marked as acknowledged.`, "Acknowledge", async () => {
            try {
                await AlertsApi.acknowledge(alert.id);
                await reloadAlerts();
                toastSuccess("Alert Acknowledged");
            } catch (e) {
                toastError("Error Acknowledging Alert", e + "");
            }
        });
    };

    const handleResolveOnClick = (alert: Alert | undefined) => {
        if (!alert) return;
        resolveDialog.show(
            async (body: Partial<ResolveBody>) => {
                await AlertsApi.resolve(alert.id, body as ResolveBody);
                await reloadAlerts();
                toastSuccess("Alert Resolved");
            },
            "Resolve Alert",
            new ResolveBody()
        );
    };

    if (!visible) {
        return (
            <DashboardLayout centerChildren="Alerts">
                <div className="text-muted-body-text">Alerts are not available yet.</div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout centerChildren="Alerts">
            <PageContainer
                Icon={Icons.AlertTriangle}
                title="Device Health Alerts"
                titleRight={
                    // No severity counts here: they only ever counted the rows the current filter had
                    // already loaded, so "0 critical, 0 warning" next to a screen full of resolved
                    // alerts was the honest-but-useless answer. The severity column and its filter say
                    // it better.
                    <div className="grid grid-cols-1 gap-3 sm:flex sm:items-center sm:gap-4 sm:flex-wrap sm:justify-end">
                        {/* The filters describe a list query, so they have nothing to narrow while a
                            single alert followed in from a notification is on screen. */}
                        {!focusedAlertId && (
                        <>
                        {canSeeAllOrganizations && (
                            // Two states, not an organization picker: the dashboard already has one of
                            // those in the top nav, and a second would leave "which organization am I
                            // in" with two answers. Narrowing to one other organization is the
                            // Organization column's filter instead.
                            <Select
                                className="w-full sm:w-56"
                                value={allOrganizations ? "all" : "current"}
                                onChange={(e) => setAllOrganizations(e.target.value === "all")}
                                options={[
                                    { value: "current", label: "This Organization" },
                                    { value: "all", label: "All Organizations" },
                                ]}
                            />
                        )}
                        {/* Location and device are server-side filters over ONE organization's option
                            lists. Fleet-wide they'd have to list every location and device in the
                            system, so that view narrows with the Organization column's filter and the
                            table's own Location / Device / Message filters instead. */}
                        {!allOrganizations && (
                        <>
                        <Select
                            className="w-full sm:w-48"
                            emptyOptionText="All Locations"
                            emptyOptionDisabled={false}
                            value={facilityId}
                            onChange={(e) => {
                                // Clear the device when the location changes: a device from the old
                                // location would combine into a filter matching nothing.
                                setFacilityId(e.target.value);
                                setDeviceId("");
                            }}
                            options={facilityOptions}
                        />
                        <Select
                            className="w-full sm:w-48"
                            emptyOptionText="All Devices"
                            emptyOptionDisabled={false}
                            value={deviceId}
                            onChange={(e) => setDeviceId(e.target.value)}
                            options={deviceOptions}
                        />
                        </>
                        )}
                        <Select
                            className="w-full sm:w-48"
                            emptyOptionText="All Non-Resolved"
                            emptyOptionDisabled={false}
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            options={[
                                { value: AlertStatus.Active, label: "Active" },
                                { value: AlertStatus.Acknowledged, label: "Acknowledged" },
                                { value: AlertStatus.Resolved, label: "Resolved" },
                            ]}
                        />
                        <Select
                            className="w-full sm:w-48"
                            emptyOptionText="Any Severity"
                            emptyOptionDisabled={false}
                            value={minSeverity}
                            onChange={(e) => setMinSeverity(e.target.value)}
                            options={[
                                { value: AlertSeverity.Info, label: "Info and up" },
                                { value: AlertSeverity.Warning, label: "Warning and up" },
                                { value: AlertSeverity.Critical, label: "Critical only" },
                            ]}
                        />
                        </>
                        )}
                        <IconLink Icon={Icons.Refresh} onClick={() => alertHelpers.refreshData()}>
                            Refresh
                        </IconLink>
                    </div>
                }
            >
                <SectionIntro title="Problems your devices have reported, and what's been done about them.">
                    <p>
                        An alert opens on its own when a device reports a fault or stops checking in, and it closes on its own
                        once the problem stops — shown as resolved with <strong>(auto)</strong>. You don't have to do anything
                        for that to happen. Subscribers are told when it starts, and when a person resolves it; a self-clearing
                        alert stays quiet unless they opt in to hearing about those under <strong>Notifications</strong>.
                    </p>
                    <p>
                        <strong>Acknowledge</strong> records that someone is looking at it, so the rest of your team knows.
                        <strong> Resolve</strong> says it's dealt with, and lets you leave a note about what was done — a device
                        that's still faulty won't reopen the same alert until the problem actually clears, so resolving a known
                        issue won't leave it fighting you.
                    </p>
                    <p>
                        Choose who gets emailed about these, and where, under <strong>Notifications</strong> in the left sidebar.
                    </p>
                    {canSeeAllOrganizations && (
                        <p>
                            <strong>All Organizations</strong> lists every open alert in the fleet, with the organization each
                            one belongs to, and the bell at the top of the page counts the same set. Alerts raised in other
                            organizations arrive here when you <strong>Refresh</strong> or reload — only the organization
                            you're currently in pushes live updates.
                        </p>
                    )}
                </SectionIntro>
                {focusedAlertId && (
                    <div className="flex items-center justify-between gap-3 p-3 mb-3 border border-separators-page bg-background-page-highlight-gray text-sm">
                        <span>Showing the one alert your notification linked to.</span>
                        <IconLink Icon={Icons.ArrowLeft} onClick={showAllAlerts}>
                            Show all alerts
                        </IconLink>
                    </div>
                )}
                <SortableTable
                    initialSortKey="openedAtUtc"
                    initialSortDir="desc"
                    isLoading={alertHelpers.isLoading}
                    emptyValue={
                        <p className="m-5">
                            {focusedAlertId
                                ? "That alert isn't available — it may belong to another organization, or have been deleted."
                                : "No Alerts Found"}
                        </p>
                    }
                    columns={[
                        {
                            header: "Severity",
                            width: 110,
                            dataKey: "severity",
                            renderFunc: (a) => (
                                <div className="flex items-center gap-2">
                                    <StatusDot status={severityDotStatus(a?.severity)} sizeClasses="w-3 h-3" />
                                    <span>{severityLabel(a?.severity)}</span>
                                </div>
                            ),
                        },
                        {
                            header: "Opened",
                            width: 180,
                            dataKey: "openedAtUtc",
                            // In the LOCATION's zone, not the viewer's. A fleet-wide list spans zones,
                            // and "02:53" means different things per row; facilityTimezoneId is
                            // carried on the row precisely so each one can say which. Falls back to
                            // the viewer's zone (still named) when a location has none set.
                            renderFunc: (a) => zonedTime(a?.openedAtUtc, a?.facilityTimezoneId || undefined),
                        },
                        {
                            header: "Type",
                            width: 180,
                            dataKey: "alertType",
                            filterable: true,
                            // Select, not text: the cell's underlying value is the numeric enum, so a
                            // text filter would only match what the user can't see.
                            filterType: "select",
                            filterOptions: alertTypeFilterOptions,
                            sortFunc: (a, b) => alertTypeLabel(a?.alertType).localeCompare(alertTypeLabel(b?.alertType)),
                            renderFunc: (a) => (a ? alertTypeLabel(a.alertType) : ""),
                        },
                        // Only in the fleet-wide view: org-scoped, every row has the same value and the
                        // column is a wasted 160px.
                        ...(allOrganizations
                            ? [
                                {
                                    header: "Organization",
                                    width: 160,
                                    dataKey: "organizationName" as const,
                                    filterable: true,
                                    filterType: "select" as const,
                                    filterOptions: organizationFilterOptions,
                                    renderFunc: (a?: Alert) => a?.organizationName ?? "—",
                                },
                            ]
                            : []),
                        {
                            header: "Location",
                            width: 150,
                            dataKey: "facilityName",
                            filterable: true,
                            filterType: "text",
                            // Only the cell renders as a link; sorting and filtering still run on dataKey's plain name.
                            renderFunc: (a) =>
                                a?.facilityId ? (
                                    <RouterLink to={Pages.FacilityDetails.getUrl({ id: a.facilityId })} className="text-link-page hover:text-link-page-hover">
                                        {a.facilityName ?? "—"}
                                    </RouterLink>
                                ) : (
                                    a?.facilityName ?? "—"
                                ),
                        },
                        {
                            header: "Device",
                            width: 150,
                            dataKey: "deviceName",
                            filterable: true,
                            filterType: "text",
                            renderFunc: (a) =>
                                a?.deviceId ? (
                                    <RouterLink to={Pages.DeviceDetails.getUrl({ id: a.deviceId })} className="text-link-page hover:text-link-page-hover">
                                        {a.deviceName ?? "—"}
                                    </RouterLink>
                                ) : (
                                    a?.deviceName ?? "—"
                                ),
                        },
                        {
                            header: "Status",
                            width: 120,
                            dataKey: "status",
                            renderFunc: (a) => (a ? enumLabel(AlertStatus[a.status] ?? "") : ""),
                        },
                        {
                            header: "Message",
                            width: 350,
                            dataKey: "message",
                            filterable: true,
                            filterType: "text",
                            renderFunc: (a) => (
                                <>
                                    <div>{a?.message ?? "—"}</div>
                                    {/* A recurring fault is now ONE row whose count climbs rather than a
                                        new alert per bounce, so this line is the only place the reader
                                        learns it has happened more than once. Without it, collapsing
                                        the rows would have hidden the flapping instead of reporting it. */}
                                    {(a?.reopenCount ?? 0) > 0 && (
                                        <div className="text-xs text-muted-body-text mt-1">
                                            Recurring — cleared and came back {a!.reopenCount} time{a!.reopenCount === 1 ? "" : "s"}
                                            {a!.lastReopenedAtUtc ? `, most recently ${zonedTime(a!.lastReopenedAtUtc, a!.facilityTimezoneId || undefined)}` : ""}
                                        </div>
                                    )}
                                    {a?.status === AlertStatus.Resolved && (
                                        <div className="text-xs text-muted-body-text mt-1">
                                            Resolved {zonedTime(a.resolvedAtUtc, a.facilityTimezoneId || undefined)}
                                            {/* Who did it, matching what the notification emails say. autoResolved and a
                                                name are mutually exclusive — nobody resolves an alert that cleared itself. */}
                                            {a.autoResolved ? " (auto)" : a.resolvedByName ? ` by ${a.resolvedByName}` : ""}
                                            {a.resolveNote ? ` — ${a.resolveNote}` : ""}
                                        </div>
                                    )}
                                    {a?.status === AlertStatus.Acknowledged && (
                                        <div className="text-xs text-muted-body-text mt-1">
                                            Acknowledged {zonedTime(a.acknowledgedAtUtc, a.facilityTimezoneId || undefined)}
                                            {a.acknowledgedByName ? ` by ${a.acknowledgedByName}` : ""}
                                        </div>
                                    )}
                                </>
                            ),
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (a) => (
                                <DropDownMenu>
                                    {a?.status === AlertStatus.Active && (
                                        <IconLink Icon={Icons.CircleSuccess} onClick={() => handleAcknowledgeOnClick(a)}>
                                            Acknowledge
                                        </IconLink>
                                    )}
                                    {a?.status !== AlertStatus.Resolved && (
                                        <IconLink Icon={Icons.Success} onClick={() => handleResolveOnClick(a)}>
                                            Resolve
                                        </IconLink>
                                    )}
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={alerts}
                    pagination
                    defaultPageSize={25}
                    pageSizeOptions={[10, 25, 50, 100]}
                    filtering
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {resolveDialog.renderDialog((_model, helpers) => (
                <div className="p-3">
                    <TextArea label="Resolve Note (optional)" rows={4} placeholder="What was done to fix this?" {...helpers.bindingsFor("note")} />
                </div>
            ))}
        </DashboardLayout>
    );
};
